import { useCallback, useEffect, useRef, useState } from "react";

import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import {
  Button,
  Chip,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import {
  ClientAppEvent,
  CommandLinkName,
  SupplierShipmentCustomerNotificationPreferenceUpdateSource,
} from "../../../constants";
import { ErrorTypography } from "../../ErrorTypography/ErrorTypography";
import { useCommandLinkExecute } from "../hooks/useCommandLinkExecute";
import { useCommandLinkPreview } from "../hooks/useCommandLinkPreview";

const CustomerShipmentNotificationPreference = {
  ALL: "ALL",
  MUTED: "MUTED",
} as const;
type CustomerShipmentNotificationPreference =
  (typeof CustomerShipmentNotificationPreference)[keyof typeof CustomerShipmentNotificationPreference];

interface UpdateCustomerNotificationPreferencePreviewData {
  shipment_id: number;
  supplier_company_name: string;
  origin_business_name: string | null;
  origin_address: string | null;
  origin_city: string | null;
  origin_province: string;
  destination_business_name: string | null;
  destination_address: string | null;
  destination_city: string | null;
  destination_province: string;
  current_preference: CustomerShipmentNotificationPreference;
}

interface UpdateCustomerNotificationPreferenceExecuteResponse {
  preference: CustomerShipmentNotificationPreference;
}

interface UpdateCustomerNotificationPreferenceLandingProps {
  token: string;
}

interface LocationLines {
  primary: string;
  secondary: string | null;
}

function getLocationLines(
  businessName: string | null,
  address: string | null,
  city: string | null,
  province: string,
): LocationLines {
  const detail = [address, city, province]
    .filter((part) => part != null && part.length > 0)
    .join(", ");
  if (businessName != null && businessName.length > 0) {
    return {
      primary: businessName,
      secondary: detail.length > 0 ? detail : null,
    };
  }
  return { primary: detail, secondary: null };
}

export function UpdateCustomerNotificationPreferenceLanding(
  props: UpdateCustomerNotificationPreferenceLandingProps,
) {
  const { token } = props;
  const posthog = usePostHog();
  const hasCapturedPreviewRef = useRef(false);

  const {
    data: previewData,
    loading: previewLoading,
    error: previewError,
  } = useCommandLinkPreview<UpdateCustomerNotificationPreferencePreviewData>({
    commandName: CommandLinkName.UPDATE_CUSTOMER_NOTIFICATION_PREFERENCE,
    token,
  });

  const [currentPreference, setCurrentPreference] =
    useState<CustomerShipmentNotificationPreference | null>(null);

  useEffect(() => {
    if (previewData && currentPreference == null) {
      setCurrentPreference(previewData.current_preference);
    }
  }, [previewData, currentPreference]);

  useEffect(() => {
    if (previewData && !hasCapturedPreviewRef.current) {
      hasCapturedPreviewRef.current = true;
      posthog.capture(ClientAppEvent.EMAIL_UNSUBSCRIBE_LINK_PREVIEWED, {
        shipment_id: previewData.shipment_id,
      });
    }
  }, [previewData, posthog]);

  const nextPreference =
    currentPreference === CustomerShipmentNotificationPreference.ALL
      ? CustomerShipmentNotificationPreference.MUTED
      : CustomerShipmentNotificationPreference.ALL;

  const {
    execute,
    loading: executeLoading,
    error: executeError,
  } = useCommandLinkExecute<
    UpdateCustomerNotificationPreferenceExecuteResponse,
    { preference: CustomerShipmentNotificationPreference }
  >({
    commandName: CommandLinkName.UPDATE_CUSTOMER_NOTIFICATION_PREFERENCE,
    token,
    params: { preference: nextPreference },
  });

  const onConfirm = useCallback(() => {
    execute((response) => {
      setCurrentPreference(response.preference);
      posthog.capture(
        ClientAppEvent.SUPPLIER_SHIPMENT_CUSTOMER_NOTIFICATION_PREFERENCE_UPDATED,
        {
          source:
            SupplierShipmentCustomerNotificationPreferenceUpdateSource.EMAIL_COMMAND_LINK,
          shipment_id: previewData?.shipment_id,
          new_preference: response.preference,
        },
      );
    });
  }, [execute, posthog, previewData]);

  if (previewError) {
    return (
      <Paper elevation={2} sx={{ p: { xs: 3, sm: 6 } }}>
        <ErrorTypography error={previewError} />
      </Paper>
    );
  }

  if (previewLoading || previewData == null || currentPreference == null) {
    return (
      <Paper elevation={2} sx={{ p: { xs: 3, sm: 6 } }}>
        <Stack spacing={2} alignItems="center">
          <Skeleton variant="circular" width={48} height={48} />
          <Skeleton variant="text" width="60%" height={36} />
          <Skeleton variant="rounded" width="40%" height={28} />
          <Skeleton variant="text" width="90%" height={20} />
          <Skeleton variant="text" width="70%" height={20} />
          <Skeleton variant="rounded" width="100%" height={42} />
        </Stack>
      </Paper>
    );
  }

  const isReceiving =
    currentPreference === CustomerShipmentNotificationPreference.ALL;
  const ctaLabel = isReceiving
    ? "Mute notifications for this shipment"
    : "Resume notifications for this shipment";
  const ctaTooltip = isReceiving
    ? "Stop receiving email notifications for this specific shipment. Notifications for other shipments are unaffected."
    : "Start receiving email notifications for this specific shipment again.";

  return (
    <Paper elevation={2} sx={{ p: { xs: 3, sm: 6 } }}>
      <Stack spacing={4}>
        <Stack spacing={2} alignItems="center">
          <MailOutlineIcon color="action" sx={{ fontSize: 56 }} />
          <Typography variant="h3" textAlign="center" fontWeight={600}>
            Notification preferences
          </Typography>
          <Chip
            label={
              isReceiving
                ? "Currently receiving notifications"
                : "Notifications muted"
            }
            color={isReceiving ? "success" : "default"}
            size="small"
          />
        </Stack>

        <Divider />

        <Stack spacing={1.5}>
          <Stack spacing={0.25}>
            <Typography variant="h5" fontWeight={600}>
              {previewData.supplier_company_name}
            </Typography>
            <Typography variant="h6" color="text.secondary" fontWeight={500}>
              {`Shipment #${previewData.shipment_id}`}
            </Typography>
          </Stack>

          {[
            getLocationLines(
              previewData.origin_business_name,
              previewData.origin_address,
              previewData.origin_city,
              previewData.origin_province,
            ),
            getLocationLines(
              previewData.destination_business_name,
              previewData.destination_address,
              previewData.destination_city,
              previewData.destination_province,
            ),
          ].map((lines, index) => (
            <Stack
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              direction="row"
              spacing={1.5}
              alignItems="flex-start"
            >
              <LocationOnOutlinedIcon color="action" sx={{ mt: "4px" }} />
              <Stack spacing={0.25}>
                <Typography variant="body1" fontWeight={500}>
                  {lines.primary}
                </Typography>
                {lines.secondary != null && (
                  <Typography variant="body1">{lines.secondary}</Typography>
                )}
              </Stack>
            </Stack>
          ))}
        </Stack>

        <Tooltip title={ctaTooltip}>
          <span>
            <Button
              variant="outlined"
              color={isReceiving ? "inherit" : "primary"}
              fullWidth
              onClick={onConfirm}
              disabled={executeLoading}
              sx={{ textTransform: "none", py: 1.25 }}
            >
              {ctaLabel}
            </Button>
          </span>
        </Tooltip>
        {executeError && <ErrorTypography error={executeError} />}
      </Stack>
    </Paper>
  );
}
